import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBell,
  FaBookmark,
  FaBox,
  FaCalendarAlt,
  FaClipboardList,
  FaClock,
  FaEdit,
  FaEllipsisV,
  FaHeart,
  FaList,
  FaRegTrashAlt,
  FaRupeeSign,
  FaShareAlt,
  FaShoppingCart,
  FaSyncAlt,
  FaTruck,
} from "react-icons/fa";
import { toast } from "react-toastify";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import HubFloatingRail from "../../components/hub/HubFloatingRail";
import MySavedListDetailSection from "../../components/savedLists/MySavedListDetailSection";
import {
  addMarketplaceSavedListToCart,
  deleteMarketplaceSavedList,
  fetchMarketplaceSavedListDetail,
  fetchMarketplaceSavedLists,
  renameMarketplaceSavedList,
} from "../../services/marketplaceCart.service";
import StorageService from "../../services/StorageService";
import { getViewProfileRouteByRole } from "../../utils/roleSwitcher";
import { formatCartInr, normalizeMarketplaceCart } from "../../utils/marketplaceCart.util";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/my_saved_lists.scss";
import "../../assets/styles/my_saved_list_detail.scss";

const EMPTY_LIST_DETAIL = { list: null, totalItems: 0, suppliers: [] };

const SAVED_LIST_FEATURES = [
  {
    id: "save",
    title: "Save for Later",
    description: "Save carts and items for future purchase and never lose track.",
    icon: FaBookmark,
  },
  {
    id: "share",
    title: "Share Your List",
    description: "Share your saved lists with your team or suppliers for quick collaboration.",
    icon: FaShareAlt,
  },
  {
    id: "alerts",
    title: "Get Price Alerts",
    description: "We'll notify you when prices drop on items in your saved lists.",
    icon: FaBell,
  },
];

function formatListDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function computeListMetrics(detail) {
  const normalized = normalizeMarketplaceCart(detail);
  const suppliers = normalized?.suppliers || [];
  const items = suppliers.flatMap((supplier) => supplier.items || []);
  const estimatedTotal = items.reduce(
    (sum, item) => sum + (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0),
    0
  );
  const estDelivery = suppliers[0]?.estDelivery || "5 - 10 Days";
  return { estimatedTotal, estDelivery, itemCount: items.length };
}

async function enrichSavedLists(baseLists) {
  const enriched = await Promise.all(
    baseLists.map(async (list) => {
      try {
        const detail = await fetchMarketplaceSavedListDetail(list.id);
        const metrics = computeListMetrics(detail);
        return {
          ...list,
          estimatedTotal: metrics.estimatedTotal,
          estDelivery: metrics.estDelivery,
          itemCount: metrics.itemCount || list.itemCount,
        };
      } catch {
        return {
          ...list,
          estimatedTotal: 0,
          estDelivery: "5 - 10 Days",
        };
      }
    })
  );
  return enriched;
}

function SavedListsTabs() {
  return (
    <nav className="mysavedlists-tabs" aria-label="Shopping lists">
      <Link to="/my-cart" className="mysavedlists-tabs__link">
        <FaShoppingCart aria-hidden /> My Cart
      </Link>
      <Link to="/my-wishlist" className="mysavedlists-tabs__link">
        <FaHeart aria-hidden /> My Wishlist
      </Link>
      <span className="mysavedlists-tabs__link mysavedlists-tabs__link--active" aria-current="page">
        <FaList aria-hidden /> My Saved Lists
      </span>
    </nav>
  );
}

function ListNameModal({ open, title, description, value, onChange, onClose, onSubmit, submitting, submitLabel }) {
  if (!open) return null;

  return (
    <div className="mysavedlists-modal-backdrop" role="presentation" onClick={() => !submitting && onClose()}>
      <div
        className="mysavedlists-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <h3>{title}</h3>
        <p>{description}</p>
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="List name"
          maxLength={120}
          autoFocus
        />
        <div className="mysavedlists-modal__actions">
          <button type="button" className="mysavedlists-btn mysavedlists-btn--ghost" disabled={submitting} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="mysavedlists-btn mysavedlists-btn--primary" disabled={submitting} onClick={onSubmit}>
            {submitting ? "Saving…" : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MySavedListsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { listId } = useParams();
  const menuRef = useRef(null);
  const [lists, setLists] = useState([]);
  const [detail, setDetail] = useState(EMPTY_LIST_DETAIL);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [listNameInput, setListNameInput] = useState("");

  const isDetailView = Boolean(listId);

  const loadLists = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchMarketplaceSavedLists();
      const baseLists = Array.isArray(data?.lists) ? data.lists : [];
      const enriched = await enrichSavedLists(baseLists);
      setLists(enriched);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Could not load saved lists.";
      if (!silent) toast.error(message);
      setLists([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async () => {
    if (!listId) return;
    setLoading(true);
    try {
      const data = await fetchMarketplaceSavedListDetail(listId);
      const normalized = normalizeMarketplaceCart(data);
      setDetail({
        list: data?.list || normalized?.list || null,
        totalItems: normalized?.totalItems || data?.totalItems || 0,
        suppliers: normalized?.suppliers || [],
      });
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Could not load saved list.";
      toast.error(message);
      setDetail(EMPTY_LIST_DETAIL);
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    if (!StorageService.getToken()) {
      navigate("/login", { state: { from: location.pathname } });
      return undefined;
    }
    if (isDetailView) {
      void loadDetail();
      void loadLists(true);
    } else {
      void loadLists();
    }
    return undefined;
  }, [isDetailView, loadDetail, loadLists, location.pathname, navigate]);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const onDocClick = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      setOpenMenuId(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openMenuId]);

  const allItems = useMemo(
    () => detail.suppliers.flatMap((supplier) => supplier.items || []),
    [detail.suppliers]
  );

  const defaultListId = useMemo(() => {
    if (!lists.length) return null;
    const sorted = [...lists].sort((a, b) => {
      const aTime = new Date(a.createdAt || a.updatedAt || 0).getTime();
      const bTime = new Date(b.createdAt || b.updatedAt || 0).getTime();
      return aTime - bTime;
    });
    return sorted[0]?.id ?? null;
  }, [lists]);

  const runMutation = async (fn) => {
    if (saving) return null;
    setSaving(true);
    try {
      return await fn();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Action failed.";
      toast.error(message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleRenameList = async () => {
    if (!renameTarget) return;
    const name = listNameInput.trim();
    if (!name) {
      toast.warning("Enter a list name.");
      return;
    }
    const data = await runMutation(() => renameMarketplaceSavedList(renameTarget.id, name));
    if (data) {
      toast.success("List renamed.");
      setRenameTarget(null);
      setListNameInput("");
      if (isDetailView) {
        await loadDetail();
      } else {
        setLists(await enrichSavedLists(Array.isArray(data.lists) ? data.lists : []));
      }
    }
  };

  const handleDeleteList = async (list) => {
    const confirmed = window.confirm(`Delete "${list.name}"? This cannot be undone.`);
    if (!confirmed) return;
    const data = await runMutation(() => deleteMarketplaceSavedList(list.id));
    if (data) {
      toast.info("Saved list deleted.");
      setOpenMenuId(null);
      if (isDetailView && String(list.id) === String(listId)) {
        navigate("/my-saved-lists");
        return;
      }
      setLists(await enrichSavedLists(Array.isArray(data.lists) ? data.lists : []));
    }
  };

  const handleAddToCart = async (targetListId, productIds) => {
    const data = await runMutation(() => addMarketplaceSavedListToCart(targetListId, productIds));
    if (data) {
      const count = Number(data.addedCount) || (Array.isArray(productIds) ? productIds.length : allItems.length);
      toast.success(`${count} item${count === 1 ? "" : "s"} added to cart.`);
    }
  };

  const handleShareList = async (list) => {
    if (!list?.id) return;
    const shareUrl = `${window.location.origin}/my-saved-lists/${list.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: list.name, text: "View my saved list on Trocanet", url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success("List link copied.");
    } catch {
      toast.info("Could not share list.");
    }
    setOpenMenuId(null);
  };

  const listName = detail.list?.name || "Saved List";

  return (
    <div className={`mysavedlists-page${isDetailView ? " sld-page" : ""}`}>
      <B2BHubHomeHeader />

      <main className="mysavedlists-main">
        <nav className="mysavedlists-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>›</span>
          <Link to={getViewProfileRouteByRole()}>My Account</Link>
          <span aria-hidden>›</span>
          {isDetailView ? (
            <>
              <Link to="/my-saved-lists">My Saved Lists</Link>
              <span aria-hidden>›</span>
              <span aria-current="page">{listName}</span>
            </>
          ) : (
            <span aria-current="page">My Saved Lists</span>
          )}
        </nav>

        <div className="mysavedlists-hero">
          <div className="mysavedlists-hero__left">
            {!isDetailView ? (
              <span className="mysavedlists-hero__icon" aria-hidden>
                <FaClipboardList />
              </span>
            ) : null}
            <div>
              <h1>
                {isDetailView ? (
                  <>
                    {listName}
                    <button
                      type="button"
                      className="sld-page__edit-name"
                      aria-label="Rename list"
                      disabled={saving}
                      onClick={() => {
                        setRenameTarget(detail.list);
                        setListNameInput(detail.list?.name || "");
                      }}
                    >
                      <FaEdit aria-hidden />
                    </button>
                  </>
                ) : (
                  `My Saved Lists (${lists.length})`
                )}
              </h1>
              <p className="mysavedlists-hero__subtitle">
                {isDetailView
                  ? "View and manage items in this saved list."
                  : "Manage lists saved from your cart for future purchase."}
              </p>
            </div>
          </div>
          <div className="mysavedlists-hero__actions">
            {isDetailView ? (
              <>
                <Link to="/my-saved-lists" className="mysavedlists-btn mysavedlists-btn--outline">
                  <FaArrowLeft aria-hidden /> Back to Lists
                </Link>
                <button
                  type="button"
                  className="mysavedlists-btn mysavedlists-btn--outline"
                  disabled={saving}
                  onClick={() => handleShareList(detail.list)}
                >
                  <FaShareAlt aria-hidden /> Share List
                </button>
                <button
                  type="button"
                  className="mysavedlists-btn mysavedlists-btn--primary"
                  disabled={saving || !allItems.length}
                  onClick={() => handleAddToCart(listId)}
                >
                  <FaShoppingCart aria-hidden /> Add All to Cart ({allItems.length})
                </button>
              </>
            ) : (
              <Link to="/marketplace/products" className="mysavedlists-btn mysavedlists-btn--outline">
                <FaArrowLeft aria-hidden /> Continue Shopping
              </Link>
            )}
          </div>
        </div>

        {!isDetailView ? <SavedListsTabs /> : null}

        {loading ? (
          <p className="mysavedlists-status">
            {isDetailView ? "Loading saved list…" : "Loading your saved lists…"}
          </p>
        ) : isDetailView ? (
          <MySavedListDetailSection
              listId={listId}
              detail={detail}
              setDetail={setDetail}
              allLists={lists}
              saving={saving}
              setSaving={setSaving}
              onRename={() => {
                setRenameTarget(detail.list);
                setListNameInput(detail.list?.name || "");
              }}
              onDelete={() => handleDeleteList(detail.list)}
          />
        ) : lists.length === 0 ? (
          <div className="mysavedlists-empty">
            <h2>No saved lists yet</h2>
            <p>Save selected items from your cart using &quot;Save as List&quot;.</p>
            <Link to="/my-cart" className="mysavedlists-btn mysavedlists-btn--primary">
              Go to Cart
            </Link>
          </div>
        ) : (
          <>
            <div className="mysavedlists-stack">
              {lists.map((list) => (
                <article key={list.id} className="mysavedlists-card">
                  <div className="mysavedlists-card__head">
                    <div className="mysavedlists-card__head-left">
                      <span className="mysavedlists-card__clip" aria-hidden>
                        <FaClipboardList />
                      </span>
                      <div>
                        <div className="mysavedlists-card__title-row">
                          <h2>{list.name}</h2>
                          {list.id === defaultListId ? (
                            <span className="mysavedlists-card__badge">Default</span>
                          ) : null}
                        </div>
                        <div className="mysavedlists-card__meta">
                          <span className="mysavedlists-card__meta-item">
                            <FaCalendarAlt aria-hidden /> Created on: {formatListDate(list.createdAt)}
                          </span>
                          <span className="mysavedlists-card__meta-item">
                            <FaClock aria-hidden /> Updated: {formatListDate(list.updatedAt)}
                          </span>
                          <span>{list.itemCount} item{list.itemCount === 1 ? "" : "s"}</span>
                          <span>Total: {formatCartInr(list.estimatedTotal || 0)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mysavedlists-card__menu-wrap" ref={openMenuId === list.id ? menuRef : null}>
                      <button
                        type="button"
                        className="mysavedlists-card__menu-btn"
                        aria-label={`More actions for ${list.name}`}
                        onClick={() => setOpenMenuId((current) => (current === list.id ? null : list.id))}
                      >
                        <FaEllipsisV aria-hidden />
                      </button>
                      {openMenuId === list.id ? (
                        <div className="mysavedlists-card__menu">
                          <button type="button" onClick={() => handleShareList(list)}>
                            <FaShareAlt aria-hidden /> Share
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setOpenMenuId(null);
                              setRenameTarget(list);
                              setListNameInput(list.name);
                            }}
                          >
                            <FaEdit aria-hidden /> Rename
                          </button>
                          <button type="button" className="mysavedlists-card__menu-danger" onClick={() => handleDeleteList(list)}>
                            <FaRegTrashAlt aria-hidden /> Delete
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mysavedlists-card__stats">
                    <div className="mysavedlists-card__stat">
                      <p className="mysavedlists-card__stat-label">
                        <span className="mysavedlists-card__stat-icon mysavedlists-card__stat-icon--orange" aria-hidden>
                          <FaBox />
                        </span>
                        Items
                      </p>
                      <p className="mysavedlists-card__stat-value">
                        {list.itemCount} Item{list.itemCount === 1 ? "" : "s"}
                      </p>
                    </div>
                    <div className="mysavedlists-card__stat">
                      <p className="mysavedlists-card__stat-label">
                        <span className="mysavedlists-card__stat-icon mysavedlists-card__stat-icon--green" aria-hidden>
                          <FaRupeeSign />
                        </span>
                        Estimated Total
                      </p>
                      <p className="mysavedlists-card__stat-value">{formatCartInr(list.estimatedTotal || 0)}</p>
                    </div>
                    <div className="mysavedlists-card__stat">
                      <p className="mysavedlists-card__stat-label">
                        <span className="mysavedlists-card__stat-icon mysavedlists-card__stat-icon--blue" aria-hidden>
                          <FaCalendarAlt />
                        </span>
                        Created On
                      </p>
                      <p className="mysavedlists-card__stat-value">{formatListDate(list.createdAt)}</p>
                    </div>
                    <div className="mysavedlists-card__stat">
                      <p className="mysavedlists-card__stat-label">
                        <span className="mysavedlists-card__stat-icon mysavedlists-card__stat-icon--purple" aria-hidden>
                          <FaSyncAlt />
                        </span>
                        Last Updated
                      </p>
                      <p className="mysavedlists-card__stat-value">{formatListDate(list.updatedAt)}</p>
                    </div>
                    <div className="mysavedlists-card__stat">
                      <p className="mysavedlists-card__stat-label">
                        <span className="mysavedlists-card__stat-icon mysavedlists-card__stat-icon--yellow" aria-hidden>
                          <FaTruck />
                        </span>
                        Est. Delivery
                      </p>
                      <p className="mysavedlists-card__stat-value">{list.estDelivery || "5 - 10 Days"}</p>
                    </div>
                  </div>

                  <div className="mysavedlists-card__foot">
                    <Link to={`/my-saved-lists/${list.id}`} className="mysavedlists-card__view">
                      View List <FaArrowRight aria-hidden />
                    </Link>
                    <div className="mysavedlists-card__actions">
                      <button
                        type="button"
                        className="mysavedlists-btn mysavedlists-btn--ghost"
                        disabled={saving || !list.itemCount}
                        onClick={() => handleAddToCart(list.id)}
                      >
                        <FaShoppingCart aria-hidden /> Add All to Cart
                      </button>
                      <button
                        type="button"
                        className="mysavedlists-btn mysavedlists-btn--ghost"
                        disabled={saving}
                        onClick={() => {
                          setRenameTarget(list);
                          setListNameInput(list.name);
                        }}
                      >
                        <FaEdit aria-hidden /> Rename
                      </button>
                      <button
                        type="button"
                        className="mysavedlists-btn mysavedlists-btn--danger"
                        disabled={saving}
                        onClick={() => handleDeleteList(list)}
                      >
                        <FaRegTrashAlt aria-hidden /> Delete
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <section className="mysavedlists-features" aria-label="Saved list features">
              {SAVED_LIST_FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.id} className="mysavedlists-features__item">
                    <span className="mysavedlists-features__icon" aria-hidden>
                      <Icon />
                    </span>
                    <div>
                      <p className="mysavedlists-features__title">{feature.title}</p>
                      <p className="mysavedlists-features__desc">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </section>
          </>
        )}
      </main>

      <ListNameModal
        open={Boolean(renameTarget)}
        title="Rename List"
        description={`Update the name for "${renameTarget?.name || "this list"}".`}
        value={listNameInput}
        onChange={setListNameInput}
        onClose={() => setRenameTarget(null)}
        onSubmit={handleRenameList}
        submitting={saving}
        submitLabel="Save Name"
      />

      <HubHomeFooter />
      <HubFloatingRail />
    </div>
  );
}
